import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

export type StepTransitionTimingState = 'measured' | 'insufficient_data';

export interface StepTransitionTimingRow {
  canonicalStep: string;
  /** Localized step label (this component is taal-neutraal — the caller translates). */
  label: string;
  observations: number;
  /** `null` under the observation threshold — never a schijnprecies gemiddelde (AD-16). */
  meanWorkdaysSinceReceipt: number | null;
  /** Population stddev (werkdagen); `null` under the threshold. */
  spreadWorkdays: number | null;
  state: StepTransitionTimingState;
}

export interface StepTransitionTimingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  ariaLabel: string;
  rows: StepTransitionTimingRow[];
  /** Localized "onvoldoende procesdata voor doorlooptijd" label (Story 4.2 AC2). */
  insufficientDataLabel: string;
  /** Formats the visible mean-workdays value (e.g. "2,3 werkdagen"). */
  formatWorkdays: (value: number) => string;
  /** Formats the spreiding-caption (e.g. "± 1,2 werkdagen spreiding"). */
  formatSpread: (value: number) => string;
}

/** Rounds to the nearest 5% bucket — same static-literal-width convention as `FunnelBar` (Tailwind's scanner requires literal class text). */
const WIDTH_CLASSES: Record<number, string> = {
  0: 'w-0', 5: 'w-[5%]', 10: 'w-[10%]', 15: 'w-[15%]', 20: 'w-[20%]', 25: 'w-[25%]',
  30: 'w-[30%]', 35: 'w-[35%]', 40: 'w-[40%]', 45: 'w-[45%]', 50: 'w-[50%]', 55: 'w-[55%]',
  60: 'w-[60%]', 65: 'w-[65%]', 70: 'w-[70%]', 75: 'w-[75%]', 80: 'w-[80%]', 85: 'w-[85%]',
  90: 'w-[90%]', 95: 'w-[95%]', 100: 'w-full',
};

function widthClassFor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return WIDTH_CLASSES[0]!;
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const bucket = (Math.round(pct / 5) * 5) as keyof typeof WIDTH_CLASSES;
  return WIDTH_CLASSES[bucket === 0 ? 5 : bucket] ?? WIDTH_CLASSES[100]!;
}

/**
 * Story 4.2 (FR11/FR21, UXR5) — doorlooptijd per stap (werkdagen vanaf ontvangst) MET
 * eerlijkheidsstate: een stap met >= drempel waarnemingen krijgt een doorlooptijd-staaf +
 * spreiding-caption; een stap eronder toont GEEN staaf en GEEN getal, uitsluitend het expliciete
 * `insufficientDataLabel` (`data-testid="step-transition-insufficient"`) — nooit een schijnprecies
 * gemiddelde (AD-16). Taal-neutraal: alle labels komen al vertaald van de aanroeper binnen; het
 * eerlijkheids-label is TEKST (leesbaar zonder kleur), niet uitsluitend een kleuraccent.
 */
export function StepTransitionTiming({ ariaLabel, className, formatSpread, formatWorkdays, insufficientDataLabel, rows, ...props }: StepTransitionTimingProps) {
  const measuredValues = rows.filter((row) => row.state === 'measured' && row.meanWorkdaysSinceReceipt != null).map((row) => row.meanWorkdaysSinceReceipt!);
  const max = Math.max(0, ...measuredValues);

  return (
    <div {...props} aria-label={ariaLabel} className={cn('flex flex-col gap-3', className)} data-testid="step-transition-timing" role="list">
      {rows.map((row) => {
        const isMeasured = row.state === 'measured' && row.meanWorkdaysSinceReceipt != null;
        const workdaysLabel = isMeasured ? formatWorkdays(row.meanWorkdaysSinceReceipt!) : null;

        return (
          <div className="flex flex-col gap-1" data-state={row.state} data-testid="step-transition-timing-row" key={row.canonicalStep} role="listitem">
            <div className="flex items-center justify-between text-xs text-[var(--foreground-muted)]">
              <span>{row.label}</span>
              {isMeasured ? <span className="font-semibold text-[var(--foreground)]">{workdaysLabel}</span> : null}
            </div>
            {isMeasured ? (
              <>
                <div aria-label={`${row.label}: ${workdaysLabel}`} className="h-3 w-full rounded-[var(--radius-control)] bg-[var(--background-muted)]" role="img">
                  <div className={cn('h-3 rounded-[var(--radius-control)] bg-[var(--secondary)]', widthClassFor(row.meanWorkdaysSinceReceipt!, max))} />
                </div>
                {row.spreadWorkdays != null ? <span className="text-[10px] text-[var(--foreground-muted)]">{formatSpread(row.spreadWorkdays)}</span> : null}
              </>
            ) : (
              <p className="rounded-[var(--radius-control)] border border-dashed border-[var(--border)] bg-[var(--background-muted)] px-2 py-1.5 text-[11px] text-[var(--foreground-muted)]" data-testid="step-transition-insufficient">
                {insufficientDataLabel}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
